/**
 * PPC landing page configuration.
 *
 * Each entry powers one route under /lp/*. Copy is tuned for Google Ads
 * Quality Score: keyword-rich H1 that 1:1 matches the ad headline, specific
 * benefit bullets, plain-English FAQ that pre-empts common objections, AND
 * a per-LP testimonial + CTA + form heading that mirror the ad group's
 * dominant intent (e.g. "more HVAC calls", "storm-ready roofing leads").
 *
 * Intent-match map:
 *   web-design        → "I want a quote on a new website"        → CTA: "Get My Free Quote"
 *   lead-generation   → "I want exclusive leads, not shared"     → CTA: "Get My Lead Plan"
 *   website-repairs   → "My site is broken, fix it now"          → CTA: "Get My Repair Quote"
 *   hvac              → "More HVAC service calls"                → CTA: "Get My HVAC Plan"
 *   plumbing          → "More plumbing calls now"                → CTA: "Get My Plumbing Plan"
 *   roofing           → "Storm-season roofing leads"             → CTA: "Get My Roofing Plan"
 *   law-firm          → "Better case-quality leads"              → CTA: "Get My Case Plan"
 */

import type { PpcLandingProps } from "@/components/PpcLandingPage";

type LpConfig = Omit<PpcLandingProps, "slug" | "headline"> & { headline: string };

export const PPC_LANDING_PAGES: Record<string, LpConfig> = {
  "web-design": {
    metaTitle: "Need a New Website? Free Mockup in 24 Hrs | Colorado",
    metaDescription:
      "Need a new website? Get a free custom homepage mockup in 24 hours. Mobile-first, owner-led design built right here in Colorado. No cost, no obligation.",
    eyebrow: "Colorado Web Design",
    headline: "Need a New Website? Get a Free Custom Mockup in 24 Hours",
    subheadline:
      "Tired of a slow, outdated, or template-looking site? See exactly what your new site could look like — a real custom homepage mockup, designed by Chris, delivered in 24 hours. No cost, no obligation.",
    bullets: [
      "Free custom homepage mockup — yours to keep either way",
      "Sub-2-second mobile load times on real 4G",
      "Custom-designed for your business — never a template",
      "Local SEO + schema baked in from day one",
      "Built in Colorado — talk to the actual designer",
    ],
    deliverables: [
      {
        title: "Custom design + build",
        body: "Wireframes, design, dev, and launch — all done in-house. You own the code and the hosting.",
      },
      {
        title: "Lead-first conversion",
        body: "Sticky click-to-call, prominent forms, trust signals. Built to make the phone ring.",
      },
      {
        title: "On-page SEO included",
        body: "Schema markup, meta, sitemap, page-speed work. Ranking-ready out of the gate.",
      },
    ],
    prefillMessage: "Free mockup request — web-design",
    ctaLabel: "Get My Free Mockup",
    formHeading: "Get a free homepage mockup in 24 hours",
    formSubheading:
      "No salespeople, no call centers. Chris (the owner) personally reviews your business and sends back a real custom mockup — yours to keep.",
    videoYouTubeId: "t3no84uGrms",
    videoHeading: "Meet The Owner — Watch Welcome Video",
    testimonial: {
      quote:
        "Honest pricing, no upsells, and the site loads instantly. He told us up front what we did NOT need to spend money on, which is the opposite of what every other agency tried to sell us.",
      author: "Amanda S.",
      location: "Colorado Springs, CO",
      service: "Web Design",
    },
    faqs: [
      { q: "Is the mockup really free?", a: "Yes — 100% free, no credit card, no obligation. If you love it, we'll quote a full build. If not, the mockup is yours to keep." },
      { q: "How much does a website cost?", a: "Websites start at $1,850 one-time. Every website package includes a year of hosting and a built-in traffic dashboard. See the pricing page for all packages." },
      { q: "How long does it take?", a: "Typical websites are built in less than 7 business days after content and approvals are ready. We confirm timelines for larger or custom builds before starting." },
      { q: "Will I own the website?", a: "Yes. You own your domain, your hosting, your code. We never hold anything hostage." },
    ],
  },

  "lead-generation": {
    metaTitle: "Lead Generation Colorado | Free Lead-Gen Audit",
    metaDescription:
      "Get exclusive Colorado leads — not shared lead-network lists. Free lead-gen audit shows where you're losing leads and how to fix it. No obligation.",
    eyebrow: "Colorado Lead Generation",
    headline: "Exclusive Colorado Leads — Free Lead-Gen Audit",
    subheadline:
      "Stop renting shared leads from third-party lead networks. We'll audit your current lead pipeline (SEO, Maps, ads, site) and show you exactly where leads are slipping through — free, no obligation.",
    bullets: [
      "Exclusive leads — never shared with three other contractors",
      "Free audit reveals where you're losing leads today",
      "Real call tracking + lead attribution on every channel",
      "Map Pack visibility in your Colorado service area",
      "Owner-led account — no junior account managers",
    ],
    deliverables: [
      { title: "Local SEO foundation", body: "Google Business Profile optimization, citations, on-page SEO, and review system that ranks you in your service area." },
      { title: "Google Ads (when it pays)", body: "We only run paid search when the math works — no markup on ad spend, conversion-tested landing pages." },
      { title: "Conversion-built website", body: "Sticky click-to-call, fast load, trust signals. Most agencies skip this — it's where 80% of leads are lost." },
    ],
    prefillMessage: "Free lead-gen audit request — lead-generation",
    ctaLabel: "Get My Free Audit",
    formHeading: "Get a free lead-gen audit in 24 hours",
    formSubheading:
      "Tell us your service area and goals. Chris (the owner) will personally audit your pipeline and reply within one business day with real, actionable findings.",
    testimonial: {
      quote:
        "Took over our SEO from a Denver agency charging us 3x as much for half the results. Chris is direct, no jargon, and our organic leads are up significantly six months in.",
      author: "Robert G.",
      location: "Falcon, CO",
      service: "SEO + Lead Generation",
    },
    faqs: [
      { q: "How is this different from shared lead networks like HomeAdvisor or Angi?", a: "Shared lead networks sell the same lead to 3–5 contractors who race to the bottom on price. We build you an owned pipeline (SEO + Maps + ads to your own site) so every lead is exclusively yours." },
      { q: "What's actually in the free audit?", a: "A real scorecard of your current site, Google Business Profile, Map Pack visibility, and ad performance — plus the top 3 fixes ranked by revenue impact. No fluff, no sales pitch." },
      { q: "How long until I see leads?", a: "Google Ads can produce leads in week one. SEO + Maps usually start delivering within 60–90 days and compound from there." },
      { q: "Are there contracts?", a: "No long contracts. Everything is month-to-month — if we don't perform, you leave." },
    ],
  },

  "website-repairs": {
    metaTitle: "Website Repair Colorado | Free Site Audit",
    metaDescription:
      "Broken website? Get a free site audit covering speed, broken forms, hacks, mobile glitches, and SEO problems. Fix list delivered in 24 hours.",
    eyebrow: "Website Repairs Colorado",
    headline: "Website Broken? Get a Free Site Audit in Colorado",
    subheadline:
      "Slow site? Broken forms? Hacked? Mobile glitches? We'll send a free audit listing every issue plus the top 3 fixes ranked by impact — no cost, no obligation.",
    bullets: [
      "Free site audit delivered same business day",
      "Most repairs started within 48 hours of approval",
      "WordPress, Wix, Squarespace, Shopify, or custom code",
      "Hacked site cleanup + security hardening",
      "Page-speed and Core Web Vitals fixes",
      "30-day fix-it-again guarantee on every repair",
    ],
    deliverables: [
      { title: "Diagnose first", body: "Free audit so we know exactly what's wrong before quoting. No guessing, no padded hours." },
      { title: "Fixed-price repair", body: "Most repairs quoted at a flat fee — no open-ended hourly bills. You'll know the cost up front." },
      { title: "30-day guarantee", body: "If the same issue comes back within 30 days, we fix it free. That's how confident we are." },
    ],
    prefillMessage: "Free site audit request — website-repairs",
    ctaLabel: "Get My Free Audit",
    formHeading: "Get a free site audit in 24 hours",
    formSubheading:
      "Tell us what's broken (or just paste your URL). Chris will personally audit your site today and send back a flat-fee fix plan — most repairs start within 48 hours.",
    testimonial: {
      quote:
        "Hired Chris after two other 'SEO companies' burned us. He showed up, explained exactly what was wrong with our site, fixed it, and within four months we were ranking on page one for the searches that actually drive business. No fluff, no monthly reports full of vanity metrics.",
      author: "Daniel K.",
      location: "Castle Rock, CO",
      service: "Website Fixes + Local SEO",
    },
    faqs: [
      { q: "What kind of websites do you repair?", a: "WordPress, Wix, Squarespace, Shopify, Webflow, and custom-coded sites. If it's on the internet, we can usually fix it." },
      { q: "How fast can you start?", a: "Most repairs start within 24–48 hours of approval. Hacked sites and downtime emergencies get same-day attention." },
      { q: "How much does a website repair cost?", a: "Small fixes start around $150. Most full repairs run $300–$1,500 depending on complexity. You'll get a flat quote before any work starts." },
      { q: "My site got hacked — can you help?", a: "Yes. We clean malware, restore from backups, harden security, and submit reconsideration requests to Google. Most cleanups take 24–72 hours." },
    ],
  },

  hvac: {
    metaTitle: "HVAC Marketing Colorado | Free Marketing Audit",
    metaDescription:
      "HVAC marketing built for Colorado contractors. Free marketing audit shows where you're losing service calls and how to fix it. No obligation.",
    eyebrow: "Colorado HVAC Marketing",
    headline: "More HVAC Service Calls in Colorado — Free Marketing Audit",
    subheadline:
      "Stop competing with shared lead networks. We'll audit your current HVAC marketing (Maps, SEO, ads, site) and show exactly where service calls are slipping through — free, no obligation.",
    bullets: [
      "Free audit of your Maps, SEO, and ad performance",
      "Map Pack rankings in your Colorado service area",
      "Google Ads tuned to emergency + replacement keywords",
      "Tracked calls so you know which ad booked the install",
      "Owner-led Colorado team — never outsourced",
    ],
    deliverables: [
      { title: "Local SEO + Maps", body: "Google Business Profile, service-area pages, reviews, citations — the foundation that wins the Map Pack." },
      { title: "HVAC Google Ads", body: "Emergency, replacement, and tune-up campaigns. No wasted spend on tire-kickers or job seekers." },
      { title: "Conversion-built website", body: "Sticky click-to-call, financing badges, after-hours emergency form. Built to convert HVAC traffic." },
    ],
    prefillMessage: "Free HVAC marketing audit request — hvac",
    ctaLabel: "Get My Free Audit",
    formHeading: "Get a free HVAC marketing audit in 24 hours",
    formSubheading:
      "Tell us your service area and what jobs you want more of. Chris will personally audit your marketing and reply within one business day.",
    testimonial: {
      quote:
        "Our HVAC company was getting buried by a competitor with a worse review score. Chris cleaned up our Google Business Profile, got the categories right, and helped us build a real review process. We now rank in the top 3 for our main keywords across the south Denver metro.",
      author: "Jennifer L.",
      location: "Denver, CO",
      service: "HVAC — Google Business + Local SEO",
    },
    faqs: [
      { q: "How is this different from shared lead networks (HomeAdvisor, Angi, etc.)?", a: "Shared lead networks sell the same lead to 3–5 contractors. We build you an owned pipeline — SEO + Maps + ads to your own site — so leads are exclusively yours." },
      { q: "What's actually in the free audit?", a: "A real scorecard of your Google Business Profile, Map Pack visibility, on-page SEO, and any active ad performance — plus the top 3 fixes ranked by call volume impact." },
      { q: "How fast will I see service calls?", a: "Google Ads can produce calls in week one. SEO + Maps usually take 60–90 days to deliver consistent volume and compound from there." },
      { q: "What does it cost?", a: "Monthly marketing starts at $950/month. PPC ad management is included in Expert Marketing at $2,750/month; ad spend not included. Compare packages on our pricing page." },
    ],
  },

  plumbing: {
    metaTitle: "Plumbing Marketing Colorado | Free Marketing Audit",
    metaDescription:
      "Plumbing marketing for Colorado plumbers. Free audit shows where you're losing emergency calls and high-ticket jobs. No obligation.",
    eyebrow: "Colorado Plumbing Marketing",
    headline: "Make Your Plumbing Phone Ring — Free Marketing Audit",
    subheadline:
      "Win more emergency calls and high-ticket jobs. We'll audit your current Maps, SEO, and ad performance — free — and show exactly where the phone should be ringing more.",
    bullets: [
      "Free audit of your Map Pack, SEO, and ad performance",
      "Rank in the Google Map Pack for emergency plumbing",
      "Google Ads with no markup on ad spend",
      "Call tracking + lead attribution on every channel",
      "Owner-led, Colorado-based — no offshore call centers",
    ],
    deliverables: [
      { title: "Local SEO + Map Pack", body: "Google Business Profile, citations, reviews, on-page SEO. Built to dominate emergency plumbing searches." },
      { title: "Plumbing Google Ads", body: "Drain cleaning, water heater, leak repair — campaigns tuned to high-intent keywords with negative-keyword filters." },
      { title: "Conversion-tuned site", body: "24/7 emergency form, click-to-call sticky, financing options. Designed to convert plumbing traffic." },
    ],
    prefillMessage: "Free plumbing marketing audit request — plumbing",
    ctaLabel: "Get My Free Audit",
    formHeading: "Get a free plumbing marketing audit in 24 hours",
    formSubheading:
      "Tell us your service area and what jobs you want more of (emergency, water heater, drain, etc.). Chris will personally audit your marketing and reply within one business day.",
    testimonial: {
      quote:
        "Faith-based, family-owned, actually answers the phone, and does the work themselves. I've worked with three Colorado web shops over the last 15 years — Chris is the only one I'll keep recommending.",
      author: "Tom W.",
      location: "Pueblo, CO",
      service: "Web Design + Local Maps",
    },
    faqs: [
      { q: "Will this beat shared lead networks (HomeAdvisor, Angi, etc.)?", a: "Yes. Shared lead networks sell the same lead to 3–5 plumbers. We build you exclusive leads via SEO, Maps, and your own ads." },
      { q: "What's in the free audit?", a: "A real scorecard of your Google Business Profile, Map Pack visibility, on-page SEO, and any active ads — plus the top 3 fixes ranked by call-volume impact." },
      { q: "How fast can I expect calls?", a: "Google Ads can start producing calls in week one. SEO + Maps typically take 60–90 days for steady volume." },
      { q: "What does this cost?", a: "Monthly marketing starts at $950/month. PPC ad management is included in Expert Marketing at $2,750/month; ad spend not included. Compare packages on our pricing page." },
    ],
  },

  roofing: {
    metaTitle: "Roofing Marketing Colorado | Free Marketing Audit",
    metaDescription:
      "Roofing marketing for Colorado roofers. Free audit shows where you're losing storm-season and re-roof leads. No obligation.",
    eyebrow: "Colorado Roofing Marketing",
    headline: "Storm-Season Roofing Leads — Free Marketing Audit",
    subheadline:
      "Capture more storm-damage and re-roof leads. We'll audit your current Maps, SEO, and ad performance — free — and show exactly where storm-season leads are slipping away.",
    bullets: [
      "Free audit of your Maps, SEO, and storm-season readiness",
      "Storm-ready creative on standby — launch within 24 hrs of hail",
      "Map Pack rankings in your Colorado service area",
      "Insurance-claim and free-inspection lead capture",
      "Call tracking + form attribution by source",
    ],
    deliverables: [
      { title: "Storm-season PPC", body: "Google Ads campaigns ready to spin up the moment hail hits. Conversion-tested storm landing pages." },
      { title: "Local SEO + Maps", body: "Service-area pages, reviews, GBP optimization. Win the Map Pack for roofing in your Colorado city." },
      { title: "Conversion-built website", body: "Free-inspection forms, insurance-claim guidance, financing badges. Built to convert roofing traffic." },
    ],
    prefillMessage: "Free roofing marketing audit request — roofing",
    ctaLabel: "Get My Free Audit",
    formHeading: "Get a free roofing marketing audit in 24 hours",
    formSubheading:
      "Tell us your service area and storm-season volume goals. Chris will personally audit your marketing and reply within one business day.",
    testimonial: {
      quote:
        "Chris and his team rebuilt our roofing site and got us into the Google Maps 3-pack within about 90 days. Calls have roughly doubled and we finally stopped paying a national agency that never picked up the phone. Worth every penny.",
      author: "Mike R.",
      location: "Colorado Springs, CO",
      service: "Roofing — Web Design + SEO",
    },
    faqs: [
      { q: "Can you spin up storm-season campaigns fast?", a: "Yes — we keep storm-ready ad creative on standby for active clients. We can launch within 24–48 hours of a storm event." },
      { q: "How is this different from buying shared roofing leads?", a: "Shared lead networks (such as HomeAdvisor or Angi) sell the same lead to 3–5 roofers and race to the bottom. We build you an owned pipeline so every lead is exclusively yours." },
      { q: "What's in the free audit?", a: "A real scorecard of your Google Business Profile, Map Pack visibility, on-page SEO, and storm-season ad readiness — plus the top 3 fixes ranked by impact." },
      { q: "What's the typical investment?", a: "Most roofers invest $2,000–$5,000/mo in services + their own ad budget on top. We'll quote based on your storm-season goals." },
    ],
  },

  "law-firm": {
    metaTitle: "Law Firm Marketing Colorado | Free Case-Acquisition Audit",
    metaDescription:
      "Law firm marketing for Colorado attorneys. Free case-acquisition audit shows where you're losing case-quality leads. No obligation.",
    eyebrow: "Colorado Law Firm Marketing",
    headline: "Colorado Law Firm Marketing — Free Case-Acquisition Audit",
    subheadline:
      "PPC + local SEO + conversion-built websites for Colorado attorneys. We'll audit your current case-acquisition pipeline — free — and show exactly where qualified consultations are slipping away.",
    bullets: [
      "Free audit of your firm's site, Maps, and PPC performance",
      "Case-quality PPC tuned to high-value practice areas",
      "Map Pack rankings in your Colorado city",
      "Intake-friendly forms and 24/7 click-to-call",
      "Owner-led account — no junior account managers",
    ],
    deliverables: [
      { title: "Practice-area PPC", body: "Google Ads built around your highest-value practice areas with rigorous negative keywords and intake-ready landing pages." },
      { title: "Local SEO + Maps", body: "Google Business Profile optimization, attorney bios, practice-area pages, and review acquisition that wins the Map Pack." },
      { title: "Conversion-built website", body: "Intake forms, click-to-call, attorney trust signals (bar admissions, results, awards). Built to turn visitors into consultations." },
    ],
    prefillMessage: "Free case-acquisition audit request — law-firm",
    ctaLabel: "Get My Free Audit",
    formHeading: "Get a free case-acquisition audit in 24 hours",
    formSubheading:
      "Tell us your practice area and your ideal case profile. Chris will personally audit your marketing and reply within one business day — Colorado bar advertising rules respected.",
    testimonial: {
      quote:
        "Chris built our small clinic a website that actually generates appointment requests. He's local, easy to reach, and never tried to sell us something we didn't need. Highly recommend to any Colorado small business.",
      author: "Lindsey P.",
      location: "Woodland Park, CO",
      service: "Conversion-Built Website",
    },
    faqs: [
      { q: "Do you specialize in any practice areas?", a: "We work most often with personal injury, family law, criminal defense, estate planning, and business law firms in Colorado." },
      { q: "What's in the free audit?", a: "A real scorecard of your firm's website, Google Business Profile, Map Pack visibility, and PPC performance — plus the top 3 fixes ranked by case-acquisition impact." },
      { q: "How fast will I see signed cases?", a: "Google Ads can produce qualified consultations in week one. SEO + Maps typically take 90+ days to compound but become your most cost-effective channel." },
      { q: "Will my marketing comply with state bar advertising rules?", a: "Yes. We follow Colorado bar advertising rules carefully and work with your firm's compliance review on every public-facing piece." },
    ],
  },
};

export type PpcSlug = keyof typeof PPC_LANDING_PAGES;
